import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Save, AlertTriangle, CheckCircle2, Building2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { API_URL } from "../../lib/utils";

const opts = { withCredentials: true };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-AO", { dateStyle: "short", timeStyle: "short" }) : "—");
const detail = (e, fb) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fb);

export const EmailTab = () => {
  const [status, setStatus] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [fromEmail, setFromEmail] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([axios.get(`${API_URL}/backoffice/email-settings`, opts), axios.get(`${API_URL}/backoffice/companies`, opts)]);
      setStatus(s.data); setCompanies(c.data); setFromEmail(s.data.from_email || "");
    } catch (e) { toast.error(detail(e, "Erro ao carregar definições de email")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      const r = await axios.put(`${API_URL}/backoffice/email-settings`, { from_email: fromEmail.trim() }, opts);
      toast.success(r.data.message); setStatus(r.data);
    } catch (e) { toast.error(detail(e, "Não foi possível activar o remetente")); }
    finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try { const r = await axios.post(`${API_URL}/backoffice/email-settings/test`, {}, opts); toast.success(r.data.message); load(); }
    catch (e) { toast.error(detail(e, "Falha no envio de teste")); }
    finally { setBusy(""); }
  };

  const toggle = async (c, enabled) => {
    setBusy(c.company_id);
    try {
      const r = await axios.put(`${API_URL}/backoffice/companies/${c.company_id}/email`, { enabled }, opts);
      toast.success(r.data.message);
      setCompanies((prev) => prev.map((x) => (x.company_id === c.company_id ? { ...x, email_enabled: enabled } : x)));
      setStatus((s) => (s ? { ...s, companies_disabled: s.companies_disabled + (enabled ? -1 : 1) } : s));
    } catch (e) { toast.error(detail(e, "Não foi possível alterar")); }
    finally { setBusy(""); }
  };

  const list = companies.filter((c) => !search || (c.name || "").toLowerCase().includes(search.toLowerCase()) || (c.nif || "").includes(search));
  const changed = status && fromEmail.trim().toLowerCase() !== (status.from_email || "");

  return (
    <div className="space-y-6" data-testid="backoffice-email-tab">
      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-4" data-testid="platform-sender-card">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-bold flex items-center gap-2"><Mail className="w-5 h-5 text-cyan-400" /> Remetente da plataforma</h2>
          {status && (
            <Badge className={status.configured && !status.test_mode ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"} data-testid="platform-sender-status">
              {!status.api_key_present ? "Sem RESEND_API_KEY" : !status.from_email ? "Sem remetente" : status.test_mode ? "Remetente de teste" : "Activo"}
            </Badge>
          )}
          {status?.source && <span className="text-xs text-white/40">origem: {status.source === "backoffice" ? "Backoffice" : "servidor (.env)"}</span>}
        </div>
        <p className="text-sm text-white/60">
          Configuração automática para todas as empresas: cada uma envia como <strong className="text-white/80">«Nome da Empresa &lt;{status?.from_email || "remetente"}&gt;»</strong> e as respostas dos clientes vão para o email dessa empresa (reply-to). As empresas não configuram nada.
        </p>
        {status?.test_mode && (
          <p className="text-sm text-amber-300 flex items-start gap-2" data-testid="platform-sender-test-mode">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> O remetente actual é de teste da Resend e só entrega ao seu próprio email. Indique abaixo um endereço do domínio verificado (ex. faturas@lumorasoftangola.com).
          </p>
        )}
        {status && !status.api_key_present && (
          <p className="text-sm text-red-300 flex items-start gap-2" data-testid="platform-sender-no-key"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> A chave RESEND_API_KEY não existe no servidor — adicione-a nos Secrets do deploy e republique.</p>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="faturas@lumorasoftangola.com" className="bg-white/5 border-white/15 text-white placeholder:text-white/30 sm:max-w-md" data-testid="platform-sender-input" />
          <Button onClick={save} disabled={busy === "save" || !fromEmail.trim() || !changed} className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" data-testid="platform-sender-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {busy === "save" ? "A testar e guardar…" : "Guardar e testar"}
          </Button>
          <Button variant="outline" onClick={test} disabled={busy === "test" || !status?.configured} className="border-white/15 bg-transparent text-white/80 hover:bg-white/10" data-testid="platform-sender-test-btn">
            <Send className="w-4 h-4 mr-1.5" /> {busy === "test" ? "A enviar…" : "Enviar teste"}
          </Button>
        </div>
        <p className="text-xs text-white/40">O remetente só fica activo depois de um email de teste chegar com sucesso ao seu email. Fica guardado na plataforma (não precisa de republicar).{status?.last_test ? ` Último teste: ${fmt(status.last_test.at)} → ${status.last_test.to}` : ""}</p>
      </section>

      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 space-y-3" data-testid="company-email-list">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-bold flex items-center gap-2"><Building2 className="w-5 h-5 text-cyan-400" /> Email aos clientes por empresa</h2>
          {status && <span className="text-xs text-white/40">{status.companies_total - status.companies_disabled} activa(s) · {status.companies_disabled} desactivada(s)</span>}
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Procurar empresa ou NIF" className="ml-auto bg-white/5 border-white/15 text-white placeholder:text-white/30 sm:max-w-xs" data-testid="company-email-search" />
        </div>
        <ul className="divide-y divide-white/5">
          {list.map((c) => (
            <li key={c.company_id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`company-email-row-${c.company_id}`}>
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{c.name} <span className="text-white/30 font-normal">· {c.nif || "sem NIF"}</span></p>
                <p className="text-xs text-white/40 truncate">Remetente automático: {c.email_sender_name || c.name} &lt;{status?.from_email || "—"}&gt; · respostas → {c.email_reply_to || <span className="text-amber-300">sem email da empresa</span>}</p>
              </div>
              {c.email_enabled ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <span className="text-[10px] uppercase text-red-300 shrink-0">desligado</span>}
              <Switch checked={c.email_enabled !== false} disabled={busy === c.company_id} onCheckedChange={(v) => toggle(c, v)} className="data-[state=unchecked]:bg-white/20" data-testid={`company-email-switch-${c.company_id}`} />
            </li>
          ))}
          {list.length === 0 && <li className="py-3 text-xs text-white/30">Sem empresas</li>}
        </ul>
      </section>
    </div>
  );
};
